/**
 * Toast (aviso flutuante) padronizado — substitui o `alert()` nativo (que
 * quebra dark mode e a identidade premium da marca) para mensagens de
 * sucesso/erro/info que NÃO exigem decisão do usuário. Para confirmação
 * (sim/não), use [[ConfirmDialog]] / useConfirm.
 *
 * Store mínima em módulo (sem lib externa): pode ser chamada de QUALQUER lugar,
 * inclusive fora de componente (ex.: o MutationCache global em main.tsx usa
 * notifyError). Em componentes, prefira o hook:
 *
 *   const toast = useToast();
 *   toast.success("Tarefa criada");
 *   toast.error("Não foi possível enviar");
 *   toast({ title: "Pronto", description: "Detalhe", tone: "info" });
 *
 * Monte <ToastHost /> UMA vez perto da raiz (main.tsx).
 */
import { useEffect, useState } from "react";
import { X, AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";

export type ToastTone = "success" | "error" | "warn" | "info";

export interface ToastOptions {
  title: string;
  description?: string;
  tone?: ToastTone;
  duration?: number; // ms
  /** Ação inline (ex.: "Desfazer" depois de excluir). Clicar dispara e fecha. */
  action?: { label: string; onClick: () => void };
}

interface ToastItem extends Required<Pick<ToastOptions, "title" | "tone" | "duration">> {
  id: number;
  description?: string;
  action?: { label: string; onClick: () => void };
}

const TONE_META: Record<ToastTone, { icon: typeof CheckCircle2; cor: string; barra: string }> = {
  success: { icon: CheckCircle2, cor: "text-emerald-600", barra: "bg-emerald-500" },
  error: { icon: XCircle, cor: "text-rose-600", barra: "bg-rose-500" },
  warn: { icon: AlertTriangle, cor: "text-amber-600", barra: "bg-amber-500" },
  info: { icon: Info, cor: "text-blue-600", barra: "bg-blue-500" },
};

let _id = 0;
const listeners = new Set<(t: ToastItem[]) => void>();
let toasts: ToastItem[] = [];

function emit() {
  for (const l of listeners) l(toasts);
}

function dismiss(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

/** Dispara um toast. Chamável de qualquer lugar (componente ou não). */
export function notify(opts: ToastOptions) {
  const id = ++_id;
  const tone = opts.tone ?? "info";
  // Toast com ação (ex.: "Desfazer") fica mais tempo pra dar tempo de clicar.
  const duration = opts.duration ?? (opts.action ? 8000 : tone === "error" ? 6000 : 3500);
  toasts = [...toasts, { id, title: opts.title, description: opts.description, tone, duration, action: opts.action }];
  emit();
  setTimeout(() => dismiss(id), duration);
}

/** Back-compat: atalho de erro usado pelo MutationCache global (main.tsx). */
export function notifyError(msg: string) {
  notify({ title: msg, tone: "error" });
}

/** Hook ergonômico pra usar em componentes. */
export interface ToastApi {
  (opts: ToastOptions): void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  warn: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

export function useToast(): ToastApi {
  const api = ((opts: ToastOptions) => notify(opts)) as ToastApi;
  api.success = (title, description) => notify({ title, description, tone: "success" });
  api.error = (title, description) => notify({ title, description, tone: "error" });
  api.warn = (title, description) => notify({ title, description, tone: "warn" });
  api.info = (title, description) => notify({ title, description, tone: "info" });
  return api;
}

/** Monte UMA vez perto da raiz da app. */
export function ToastHost() {
  const [items, setItems] = useState<ToastItem[]>(toasts);
  useEffect(() => {
    listeners.add(setItems);
    return () => {
      listeners.delete(setItems);
    };
  }, []);

  if (!items.length) return null;
  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 w-[min(92vw,360px)] pointer-events-none">
      {items.map((t) => {
        const meta = TONE_META[t.tone];
        const Icon = meta.icon;
        return (
          <div
            key={t.id}
            role={t.tone === "error" ? "alert" : "status"}
            className="pointer-events-auto relative overflow-hidden bg-card border rounded-lg shadow-lg flex gap-3 p-3 pr-8"
          >
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${meta.barra}`} />
            <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${meta.cor}`} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium leading-tight">{t.title}</div>
              {t.description && (
                <div className="text-xs text-muted-foreground mt-0.5">{t.description}</div>
              )}
              {t.action && (
                <button
                  onClick={() => {
                    t.action!.onClick();
                    dismiss(t.id);
                  }}
                  className="mt-1.5 text-xs font-semibold text-primary hover:underline"
                >
                  {t.action.label}
                </button>
              )}
            </div>
            <button
              onClick={() => dismiss(t.id)}
              aria-label="Fechar"
              className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
